import { Box, Stack } from "@mui/material";
import { useTranslation } from "next-i18next";
import { IconAlphaLight } from "src/components/icons-mui/alpha-light";
import { IconAlphaLightMinimized } from "src/components/icons-mui/alpha-light-minimized";
import { IconAlphaStrong } from "src/components/icons-mui/alpha-strong";
import { IconAlphaStrongMinimized } from "src/components/icons-mui/alpha-strong-minimized";
import { IconLowBeta } from "src/components/icons-mui/low-beta";
import { IconLowBetaMinimized } from "src/components/icons-mui/low-beta-minimized";
import { useTagTooltips } from "src/components-mui/base/table-scrollable/components/entity/tag-tooltips-context";
import { useTableScrollableSnapshot } from "src/components-mui/base/table-scrollable/store/table-scrollable-store";
import { Typography } from "src/components-mui/base/typography/typography";
import { WfPopover } from "src/components-mui/base/wf-popover/wf-popover";
import { ITooltip } from "src/types/common";
import type { ITagIconTag } from "./tag-icon.types";

export type { ITagIconTag };

interface ITagConfig {
    circleIcon: JSX.Element;
    pillIcon: JSX.Element;
    /** Self-bordered 24x24 round icon (rangliste style), same footprint as the invested pie icon. */
    roundIcon: JSX.Element;
    label: string;
    /** Measured rendered pill width in px at lg+ (border + padding + icon + gap + label). */
    pillWidth: number;
}

// Outperformance tags: shown as pill (lg+) or minimized pill (md/sm depending on surface), hidden at xs
// circleIcon: tight 14x14 Figma glyph rendered inside CIRCLE_PILL_SX (8px/4px padding per design)
// pillIcon: hideBorder crops the SVG side whitespace, so icon widths match each glyph's aspect ratio at 24px height
const OUTPERFORMANCE_TAG_CONFIG: Partial<Record<string, ITagConfig>> = {
    "alpha-strong": {
        circleIcon: <IconAlphaStrongMinimized sx={{ width: "14px", height: "14px" }} />,
        pillIcon: <IconAlphaStrong hideBorder sx={{ width: "14.4px", height: "24px" }} />,
        roundIcon: <IconAlphaStrong sx={{ width: "24px", height: "24px" }} />,
        label: "Alpha Strong",
        pillWidth: 111,
    },
    "alpha-light": {
        circleIcon: <IconAlphaLightMinimized sx={{ width: "14px", height: "14px" }} />,
        pillIcon: <IconAlphaLight hideBorder sx={{ width: "10px", height: "24px" }} />,
        roundIcon: <IconAlphaLight sx={{ width: "24px", height: "24px" }} />,
        label: "Alpha",
        pillWidth: 65,
    },
    "low-beta": {
        circleIcon: <IconLowBetaMinimized sx={{ width: "14px", height: "14px" }} />,
        pillIcon: <IconLowBeta hideBorder sx={{ width: "7px", height: "24px" }} />,
        roundIcon: <IconLowBeta sx={{ width: "24px", height: "24px" }} />,
        label: "Low Beta",
        pillWidth: 81,
    },
};

const TAG_PILL_GAP = 8; // Stack gap={1} between pills at lg

// Widest possible combo: alpha-strong + low-beta
const MAX_TAGS_COMBO_WIDTH = OUTPERFORMANCE_TAG_CONFIG["alpha-strong"]!.pillWidth + TAG_PILL_GAP + OUTPERFORMANCE_TAG_CONFIG["low-beta"]!.pillWidth;

/** Right padding in px that pushes this row's pills left so pills start at the same x across all rows (widest combo gets 0). */
export const getTagsAlignmentPaddingRight = (tags?: string[]): number => {
    const pillWidths = (tags ?? []).filter(tag => tag in OUTPERFORMANCE_TAG_CONFIG).map(tag => OUTPERFORMANCE_TAG_CONFIG[tag]!.pillWidth);
    if (pillWidths.length === 0) {
        return 0;
    }
    const rowTagsWidth = pillWidths.reduce((sum, width) => sum + width, 0) + TAG_PILL_GAP * (pillWidths.length - 1);
    return Math.max(0, MAX_TAGS_COMBO_WIDTH - rowTagsWidth);
};

const ROUND_TAG_SIZE = 24;
const ROUND_TAG_GAP = 4; // Stack gap={0.5} between round icons

// Widest possible round combo: two icons (alpha variants are mutually exclusive with each other, not with low-beta)
export const MAX_ROUND_TAGS_COMBO_WIDTH = 2 * ROUND_TAG_SIZE + ROUND_TAG_GAP;

export const hasOutperformanceTags = (tags?: string[]): boolean => tags?.some(tag => tag in OUTPERFORMANCE_TAG_CONFIG) ?? false;

const PILL_SX = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    border: "1px solid",
    borderColor: "teal.700",
    borderRadius: "100px",
    paddingX: 1,
    height: "26px",
} as const;

// Exact Figma spec for the minimized tag: 14x14 glyph + 8px horizontal / 4px vertical padding
const CIRCLE_PILL_SX = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid",
    borderColor: "teal.700",
    borderRadius: "100px",
    paddingX: "8px",
    paddingY: "4px",
} as const;

export interface ITagIconProps {
    tableTags?: ITagIconTag[];
    tags?: string[];
    /** When true, circle icons are shown from sm (768px) upward instead of md (992px) only. Use for extended search. */
    showCircleFromSm?: boolean;
    /** When "round", the self-bordered 24px round icons (rangliste style) are rendered on all visible breakpoints instead of circles/pills. */
    variant?: "round";
}

interface IResolvedTag {
    key: string;
    circleIcon: JSX.Element;
    roundIcon?: JSX.Element;
    pillIcon?: JSX.Element;
    label?: string;
    tooltip?: ITooltip;
}

export const TagIcon = ({ tableTags, tags, showCircleFromSm, variant }: ITagIconProps) => {
    const { t } = useTranslation("common");
    const tagTooltips = useTagTooltips();
    const { shrinkFirstColumn } = useTableScrollableSnapshot();
    const resolved: IResolvedTag[] = [];

    const pushResolved = (tag: IResolvedTag) => {
        if (resolved.some(existing => existing.key === tag.key)) {
            return;
        }
        resolved.push(tag);
    };

    if (tableTags) {
        for (const tableTag of tableTags) {
            if (!tags?.includes(tableTag.tagUrlIdentifier)) {
                continue;
            }
            const config = OUTPERFORMANCE_TAG_CONFIG[tableTag.tagUrlIdentifier];
            if (config) {
                pushResolved({
                    key: tableTag.tagUrlIdentifier,
                    circleIcon: config.circleIcon,
                    roundIcon: config.roundIcon,
                    pillIcon: config.pillIcon,
                    label: config.label,
                    tooltip: tableTag.tooltip,
                });
            }
        }
    } else {
        for (const tagId of tags ?? []) {
            const config = OUTPERFORMANCE_TAG_CONFIG[tagId];
            if (config) {
                const tooltip = tagTooltips?.find(tagTooltip => tagTooltip.tagUrlIdentifier === tagId)?.tooltip;
                pushResolved({
                    key: tagId,
                    circleIcon: config.circleIcon,
                    roundIcon: config.roundIcon,
                    pillIcon: config.pillIcon,
                    label: config.label,
                    tooltip,
                });
            }
        }
    }

    if (resolved.length === 0 || shrinkFirstColumn) {
        return null;
    }

    const renderRoundIcon = ({ key, roundIcon, label, tooltip }: IResolvedTag) => {
        if (tooltip) {
            return (
                <WfPopover
                    key={key}
                    content={tooltip.content}
                    PopoverTrigger={
                        <Box sx={{ display: "inline-flex", cursor: "pointer" }} aria-label={t("labels.tag-with-name", { 0: label })}>
                            {roundIcon}
                        </Box>
                    }
                    link={tooltip.link}
                />
            );
        }
        return (
            <Box key={key} sx={{ display: "inline-flex" }} aria-label={t("labels.tag-with-name", { 0: label })}>
                {roundIcon}
            </Box>
        );
    };

    const renderCircleIcon = ({ key, circleIcon, label, tooltip }: IResolvedTag) => {
        if (tooltip) {
            return (
                <WfPopover
                    key={key}
                    content={tooltip.content}
                    PopoverTrigger={
                        <Box sx={{ ...CIRCLE_PILL_SX, cursor: "pointer" }} aria-label={t("labels.tag-with-name", { 0: label })}>
                            {circleIcon}
                        </Box>
                    }
                    link={tooltip.link}
                />
            );
        }
        return (
            <Box key={key} sx={CIRCLE_PILL_SX} aria-label={t("labels.tag-with-name", { 0: label })}>
                {circleIcon}
            </Box>
        );
    };

    const renderPill = ({ key, pillIcon, label, tooltip }: IResolvedTag) => {
        if (!pillIcon || !label) {
            return null;
        }
        const pillContent = (
            <>
                {pillIcon}
                <Typography
                    noWrap
                    sx={{
                        fontSize: "xs",
                        lineHeight: 1.1,
                        color: "teal.700",
                        fontWeight: "bold",
                    }}
                >
                    {label}
                </Typography>
            </>
        );
        if (tooltip) {
            return (
                <WfPopover
                    key={key}
                    content={tooltip.content}
                    PopoverTrigger={<Box sx={{ ...PILL_SX, cursor: "pointer" }}>{pillContent}</Box>}
                    link={tooltip.link}
                />
            );
        }
        return (
            <Box key={key} sx={PILL_SX}>
                {pillContent}
            </Box>
        );
    };

    if (variant === "round") {
        return (
            <Stack
                direction="row"
                sx={{
                    gap: 0.5,
                    display: showCircleFromSm ? { xs: "none", sm: "flex" } : { xs: "none", md: "flex" },
                }}
            >
                {resolved.map(renderRoundIcon)}
            </Stack>
        );
    }

    return (
        <>
            {/* circle icons: md only for watchlist, sm+ for extended search */}
            <Stack
                direction="row"
                sx={{
                    gap: 0.5,
                    display: showCircleFromSm ? { xs: "none", sm: "flex", lg: "none" } : { xs: "none", md: "flex", lg: "none" },
                }}
            >
                {resolved.map(renderCircleIcon)}
            </Stack>
            {/* lg+ (1200px+): pill labels */}
            <Stack
                direction="row"
                sx={{
                    gap: 1,
                    display: showCircleFromSm ? { xs: "none", sm: "none", lg: "flex" } : { xs: "none", md: "none", lg: "flex" },
                }}
            >
                {resolved.map(renderPill)}
            </Stack>
        </>
    );
};
